import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Alert } from "../components/ui/alert";
import Navbar from "../components/ui/Navbar";
import UserForm from "../components/ui/UserForm";
import { toast } from "react-toastify";
import axios from "axios";
import {
  LoaderPinwheel,
  Users,
  Plus,
  Search,
  X,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import PageSizeSelect from "../components/ui/PageSizeSelect";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Filtros y paginación
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL;

  // Computed data
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        !search ||
        user.name?.toLowerCase().includes(search.toLowerCase()) ||
        user.email?.toLowerCase().includes(search.toLowerCase());
      const matchesRole =
        !filterRole ||
        user.role?.name?.toLowerCase() === filterRole.toLowerCase();
      return matchesSearch && matchesRole;
    });
  }, [users, search, filterRole]);

  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const pagedUsers = filteredUsers.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const hasActiveFilters = search.trim() || filterRole.trim();

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setFilterRole("");
    setPage(1);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(res.data);
    } catch (err) {
      setError("Error al obtener usuarios");

      const msg =
        err.response?.data?.message || err.message || "Error desconocido";

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try {
      await axios.delete(`${API_URL}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchUsers();
      toast.info("Usuario eliminado");
    } catch (err) {
      toast.error("Error al eliminar usuario");
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      let res;

      if (selectedUser) {
        res = await axios.put(
          `${API_URL}/users/${selectedUser._id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } else {
        res = await axios.post(`${API_URL}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      toast.success(res.data?.message || "Usuario guardado");
      fetchUsers();

      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Error al guardar el usuario";
      toast.error(msg);
      throw err;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28 space-y-6">
        {/* Hero Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
              <p className="text-sm text-gray-500">
                {filteredUsers.length} usuario
                {filteredUsers.length !== 1 ? "s" : ""}{" "}
                {hasActiveFilters ? "filtrado" : "activo"}
                {filteredUsers.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <Button onClick={handleCreate} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Nuevo usuario
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Nombre o correo..."
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Rol
              </label>
              <select
                value={filterRole}
                onChange={(e) => {
                  setFilterRole(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:outline-none"
              >
                <option value="">Todos los roles</option>
                <option value="admin">Admin</option>
                <option value="user">Usuario</option>
                <option value="tecnico">Técnico</option>
              </select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full font-medium">
                Filtros activos · {pagedUsers.length} resultado
                {pagedUsers.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={clearFilters}
                className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Limpiar
              </button>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <p>{error}</p>
          </Alert>
        )}

        {loading && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100">
            <PageSizeSelect
              value={pageSize}
              onChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                  <th className="py-3 px-4 text-left font-semibold">Nombre</th>
                  <th className="py-3 px-4 text-left font-semibold">Correo</th>
                  <th className="py-3 px-4 text-left font-semibold">Rol</th>
                  <th className="py-3 px-4 text-right font-semibold">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-gray-500">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="font-medium">No se encontraron usuarios</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {hasActiveFilters
                          ? "Ajusta los filtros o limpia la búsqueda"
                          : "Crea tu primer usuario"}
                      </p>
                    </td>
                  </tr>
                ) : (
                  pagedUsers.map((user) => (
                    <tr
                      key={user._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-4 font-medium text-gray-900">
                        {user.name}
                      </td>
                      <td className="py-4 px-4 text-gray-700 max-w-xs truncate">
                        {user.email}
                      </td>
                      <td className="py-4 px-4">
                        {user.role?.name ? (
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              user.role.name.toLowerCase() === "admin"
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : user.role.name.toLowerCase() === "tecnico"
                                  ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                  : "bg-green-50 text-green-700 border-green-200"
                            }`}
                          >
                            {user.role.name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs italic">
                            Sin rol
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1 h-9 px-3 text-xs"
                            onClick={() => handleEdit(user)}
                          >
                            <Pencil className="h-3 w-3" />
                            Editar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="flex items-center gap-1 h-9 px-3 text-xs"
                            onClick={() => handleDelete(user._id)}
                          >
                            <Trash2 className="h-3 w-3" />
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <span className="text-xs text-gray-400">
              {(page - 1) * pageSize + 1}–
              {Math.min(page * pageSize, filteredUsers.length)} de{" "}
              {filteredUsers.length}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-9 px-3 text-xs"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Anterior
              </Button>
              <span className="px-3 py-1.5 bg-gray-100 rounded-md text-xs font-medium min-w-[60px] text-center">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-9 px-3 text-xs"
              >
                Siguiente
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <UserForm
          user={selectedUser}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          onSaved={() => fetchUsers()}
        />
      )}
    </div>
  );
};

export default UsersPage;
